import ApiService from '../../../../../services/apiService';
import { mapState } from 'vuex';
import { isNullOrEmpty } from '../../../../../utils/vuex';
import { logException } from '../../../../../sentry';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
export default {
  name: 'CoreDtwinStateBridgeNiota',
  props: {
    // the current edited binding
    lclModel: {
      type: Object,
      required: true
    },
    bridgeConfig: {
      type: Object,
      required: true
    },
    // is this an 'edit', 'create', or mass-variable- 'import'?
    mode: {
      type: String,
      required: false,
      default: () => 'edit'
    }
  },
  data: function () {
    return {
      // the current result-set for the autocomplete device search
      niotaDevices: [],
      loading: false,
      loadingDevices: false,
      niotaDeviceSearch: '',
      selectedNiotaDevice: null,
      niotaDeviceVariableHeaders: [{
        sortable: true,
        text: 'Name',
        value: 'variable',
        align: 'left'
      }, {
        sortable: true,
        text: this.$i18n_t('DigitalTwin.DigitalTwin.states.value'),
        value: 'value',
        align: 'left'
      }, {
        sortable: true,
        text: this.$i18n_t('DigitalTwin.DigitalTwin.states.lastUpdate'),
        value: 'milliseconds_ago',
        align: 'left'
      }, {
        sortable: true,
        text: this.$i18n_t('DigitalTwin.DigitalTwin.states.unit'),
        value: 'value_type_name',
        align: 'left'
      }],
      niotaDeviceVariableFooterProps: {
        itemsPerPageText: this.$i18n_t('Common.rowsPerPage'),
        itemsPerPageOptions: [20, 50, {
          text: this.$i18n_t('Common.all'),
          value: -1
        }]
      },
      // the retrieved variables for the selected device
      niotaDeviceVariables: null,
      selectedNiotaDeviceVariable: []
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme']),
    formIsValid() {
      return !isNullOrEmpty(this.selectedNiotaDevice) && !isNullOrEmpty(this.selectedNiotaDeviceVariable) && this.selectedNiotaDeviceVariable.length > 0;
    }
  },
  watch: {
    niotaDeviceSearch(val) {
      if (!val || val.length < 2) {
        // if search value is empty or less than two characters do not start a search
        // but treat the old search as invalid as a new search is as about to happen
        // in this state no results will be shown in the auto-complete dropdown
        // because we set `niotaDevices` to an array with only `selectedNiotaDevice`
        // which will not show up because auto-completes `hide-selected` is true
        // while `loadingDevices` is true or the search is empty or less than two characters
        this.niotaDevices = [this.selectedNiotaDevice].filter(d => d);
        return;
      } else if (!this.bridgeConfig || !this.bridgeConfig.configId) {
        // if no niota account is selected do nothing
        return;
      }

      // do not trigger the device-search (again) after the item has been selected
      // that would clear the variable-set
      if (!this.selectedNiotaDevice || !this.selectedNiotaDevice.title || val !== this.selectedNiotaDevice.title) {
        // set loadingDevices to true as early as the debounced fetchDevices will be called
        // to not delay the loading state when settings it from the debounced method
        this.loadingDevices = true;
        this.fetchDevices();
      }
    },
    bridgeConfig(val) {
      if (val) {
        this.$nextTick(() => {
          const deviveInput = this.$refs.niotaDevice.$el.querySelector('input');
          deviveInput.focus();
        });
        this.emitSelection();
      }
    },
    selectedNiotaDevice(val) {
      if (val !== null) {
        this.fetchVariables();
      }
      this.emitSelection();
    },
    selectedNiotaDeviceVariable() {
      this.emitSelection();
    }
  },
  created() {
    if (this.mode !== 'create' && this.mode !== 'import') {
      if (get(this.bridgeConfig, 'configId') && get(this.lclModel, 'properties.binding.options.identifier')) {
        ApiService.getNiotaDevice(this.bridgeConfig.configId, this.lclModel.properties.binding.options.identifier).then(device => {
          if (!device || !device.id) {
            throw new Error('NIOTA_DEVICE_NOT_FOUND');
          }
          this.niotaDevices = device ? [device] : null;
          this.selectedNiotaDevice = device;
        }).catch(error => {
          let errorMessage = get(error, 'response.data.message', error.message);
          let errorOutput = this.$i18n_te(`Error.${errorMessage}`) ? this.$i18n_t(`Error.${errorMessage}`) : this.$i18n_t(errorMessage);
          this.$snack(errorOutput, 'error');
          logException(error);
          this.niotaDevices = null;
          this.selectedNiotaDevice = null;
        }).finally(() => {
          this.loading = false;
        });
      } else {
        this.loading = false;
      }
      this.selectedNiotaDeviceVariable = [{
        variable: this.lclModel.properties.binding.options.variable
      }];
    }
  },
  methods: {
    emitSelection() {
      this.$emit('bridge-binding-changed', {
        valueType: get(this.selectedNiotaDeviceVariable, '[0].value_type_name'),
        variable: get(this.selectedNiotaDeviceVariable, '[0].variable'),
        value: get(this.selectedNiotaDeviceVariable, '[0].value')
      });
    },
    fetchDevices: debounce(function () {
      this.loadingDevices = true;
      ApiService.fetchNiotaDevices(this.bridgeConfig.configId, this.niotaDeviceSearch).then(devices => {
        // only allow devices with a devicetype, as otherwise there are no parsed variables to choose from
        this.niotaDevices = devices.filter(device => {
          return device.deviceTypeId;
        });
      }).catch(error => {
        let errorMessage = get(error, 'response.data.message', error.message);
        let errorOutput = this.$i18n_te(`Error.${errorMessage}`) ? this.$i18n_t(`Error.${errorMessage}`) : this.$i18n_t(errorMessage);
        this.$snack(errorOutput, 'error');
        this.niotaDevices = [];
        logException(error);
      }).finally(() => {
        this.loadingDevices = false;
      });
    }, 250),
    fetchVariables() {
      this.niotaDeviceVariables = null;
      if (this.selectedNiotaDevice === undefined || this.selectedNiotaDevice.id === undefined) {
        return;
      }
      ApiService.fetchNiotaDeviceVariables(this.bridgeConfig.configId, this.selectedNiotaDevice.id).then(variables => {
        const variableKeys = Object.keys(variables);
        this.niotaDeviceVariables = variableKeys.map(key => {
          const variable = variables[key];
          if (variable.time) {
            variable['milliseconds_ago'] = new Date() - new Date(variable.time);
            variable['time_ago'] = variable['milliseconds_ago'];
            const units = ['seconds', 'minutes', 'hours', 'days', 'months'];
            const conversion = [1000, 60, 60, 24, 30];
            let selectedUnit = 0;
            while (variable['time_ago'] >= conversion[selectedUnit] && selectedUnit < conversion.length) {
              variable['time_ago'] = Math.floor(variable['time_ago'] / conversion[selectedUnit]);
              selectedUnit++;
            }
            selectedUnit--;
            variable['time_ago'] = `${variable['time_ago']} ${units[selectedUnit]}`;
          }
          return variable;
        });
      }).catch(error => {
        let errorMessage = get(error, 'response.data.message', error.message);
        let errorOutput = this.$i18n_te(`Error.${errorMessage}`) ? this.$i18n_t(`Error.${errorMessage}`) : this.$i18n_t(errorMessage);
        this.$snack(errorOutput, 'error');
        this.niotaDeviceVariables = [];
        console.error(error);
      });
    },
    saveState() {
      this.lclModel.properties.binding.options.identifier = this.selectedNiotaDevice.id;
      this.lclModel.properties.binding.options.variable = this.selectedNiotaDeviceVariable[0].variable;
    }
  }
};